import { useEffect, useMemo, useState } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { Header } from './Header';
import { TActiveTab, TReportsHeaderDateRanges } from '@app/dashboard/types/innerLayout/reports';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { ReportsTab } from './ReportsTab';
import { ArchiveTab } from './ArchiveTab';
import { StatementsTab } from './StatementsTab';
import { DigestTab } from './DigestTab';
import styles from './reports.module.scss';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { getCurrentMode } from '@app/dashboard/utils/getCurrentMode';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import {
  REPORTS_STATEMENTS_EVENTS,
  REPORTS_STATEMENTS_EVENTS_PARAMS,
  REPORTS_STATEMENTS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/reports-statements';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';

const PAGE_TABS: { [key: string]: TActiveTab }  = {
  REPORTS: "reports",
  ARCHIVE: "archive",
  STATEMENTS: "statements",
  DIGEST: "digest"
}

export const Reports = () => {
  const { isLive } = useTestLiveModeStore();
  const [dateRange, setDateRange] = useState<TReportsHeaderDateRanges>({
    start_date: "",
    end_date: ""
  });

  const hasStatements = getFromLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS)?.enabled || false;
  const hasReports = isAccessible(PERMISSIONS.CAN_DOWNLOAD_REPORTS);
  const initialActiveTab = hasReports ? PAGE_TABS.REPORTS : hasStatements ? PAGE_TABS.STATEMENTS : PAGE_TABS.DIGEST;
  const [activeTab, setActiveTab] = useState<TActiveTab>(initialActiveTab);

  useEffect(() => {
    amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORTS_VIEWED, {
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
      [REPORTS_STATEMENTS_EVENTS_PARAMS.DEFAULT_REPORT]: 
      initialActiveTab === PAGE_TABS.REPORTS
        ? REPORTS_STATEMENTS_EVENTS_VALUES.REPORT_TYPE.REPORTS
        : initialActiveTab === PAGE_TABS.ARCHIVE
          ? REPORTS_STATEMENTS_EVENTS_VALUES.REPORT_TYPE.ARCHIVE
        : initialActiveTab === PAGE_TABS.STATEMENTS
          ? REPORTS_STATEMENTS_EVENTS_VALUES.REPORT_TYPE.STATEMENTS
          : initialActiveTab === PAGE_TABS.DIGEST
            ? REPORTS_STATEMENTS_EVENTS_VALUES.REPORT_TYPE.DIGEST
            : '',
    });
  }, []);

  const tabsChangeHandler = (tabId: TActiveTab) => {
    if (activeTab !== tabId) {
      setActiveTab(tabId);
    }
  };

  const renderTabContent = useMemo(() => {
    switch (activeTab) {
      case PAGE_TABS.REPORTS:
        return <ReportsTab headerDateRange={dateRange}/>;
      case PAGE_TABS.ARCHIVE:
        return <ArchiveTab headerDateRange={dateRange}/>;  
      case PAGE_TABS.STATEMENTS:
        return <StatementsTab/>;
      case PAGE_TABS.DIGEST:
        return <DigestTab/>;
      default:
        return null;
    }
  }, [activeTab, dateRange]);

  return (
    <div className={styles['reports-wrapper']}>
      <Header 
        activeTab={activeTab} 
        tabsChangeHandler={tabsChangeHandler} 
        setDateRange={setDateRange}
      />
      {renderTabContent}
    </div>
  );
};
